import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Target, Brain, ArrowRight, Book, History, Lightbulb, FileText, RotateCcw, CheckCircle, BookOpen, Trash } from 'lucide-react';
import { GradientTitle, AccentButton, OutlineButton, Progress } from '../components/ui';
import { RelatedBiases } from '../components/bias/RelatedBiases';
import type { CognitiveBias } from '../types';
import { Helmet } from 'react-helmet-async';

interface PreMortemPageProps {
  biases: CognitiveBias[];
}

// Types for pre-mortem
type Cause = {
  text: string;
  biasId: string;
  mitigation: string;
};

// Update the PDF generation function with improved styling and logo
const generatePreMortemPDF = (
  projectName: string, 
  projectGoals: string, 
  projectTimeline: string, 
  failureScenario: string, 
  causes: Cause[],
  biases: CognitiveBias[]
) => {
  // Create a new window to open the PDF in
  const newWindow = window.open('', '_blank');
  if (!newWindow) {
    alert('Please allow popups to view the PDF');
    return;
  }
  
  // Base URL for the website
  const baseUrl = 'https://www.cognitivebiaslab.com';
  
  // Create the HTML content for the PDF
  const htmlContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Pre-Mortem Analysis - ${projectName}</title>
        <style>
          body {
            font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
            line-height: 1.6;
            margin: 0;
            padding: 0;
            color: #333;
            background-color: #fff;
          }
          
          * {
            box-sizing: border-box;
          }
          
          .container {
            max-width: 800px;
            margin: 0 auto;
            padding: 30px;
          }
          
          .header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #f5f5f5;
          }
          
          .logo-container {
            display: flex;
            align-items: center;
          }
          
          .logo {
            height: 60px;
            margin-right: 15px;
          }
          
          .title-container {
            flex-grow: 1;
          }
          
          h1 {
            font-size: 28px;
            color: #333;
            margin: 0 0 5px 0;
            font-weight: 700;
          }
          
          .subtitle {
            color: #666;
            font-size: 14px;
            margin: 0;
          }
          
          .date {
            color: #888;
            font-size: 14px;
            text-align: right;
          }
          
          .card {
            background-color: #fff;
            border-radius: 12px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
            padding: 25px;
            margin-bottom: 25px;
            border: 1px solid #eee;
          }
          
          h2 {
            color: #e05d1f;
            font-size: 20px;
            margin-top: 0;
            margin-bottom: 15px;
            padding-bottom: 8px;
            border-bottom: 1px solid #f0f0f0;
          }
          
          h3 {
            color: #e05d1f;
            font-size: 18px;
            margin-top: 20px;
            margin-bottom: 10px;
          }
          
          .detail-grid {
            display: grid;
            grid-template-columns: 1fr 2fr;
            gap: 10px;
          }
          
          .detail-label {
            font-size: 14px;
            font-weight: 500;
            color: #666;
          }
          
          .detail-value {
            font-size: 14px;
            color: #333;
          }
          
          .causes-list {
            list-style: none;
            padding: 0;
            margin: 0;
          }
          
          .cause-item {
            padding: 15px;
            margin-bottom: 15px;
            background-color: #f9f9f9;
            border-radius: 8px;
            border: 1px solid #eee;
          }
          
          .cause-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 10px;
          }
          
          .cause-title {
            font-weight: 600;
            color: #333;
          }
          
          .bias-tag {
            display: inline-block;
            background-color: #fff2e6;
            color: #e05d1f;
            padding: 3px 10px;
            border-radius: 15px;
            font-size: 12px;
            font-weight: 500;
            text-decoration: none;
          }
          
          .bias-tag:hover {
            background-color: #ffe9d1;
          }
          
          .cause-text {
            margin-bottom: 12px;
            font-size: 14px;
          }
          
          .mitigation {
            margin-left: 15px;
            padding-left: 10px;
            border-left: 2px solid #e05d1f;
            margin-top: 12px;
            font-size: 14px;
          }
          
          .mitigation-title {
            font-weight: 500;
            color: #666;
            margin-bottom: 5px;
            font-size: 13px;
          }
          
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #eee;
            color: #888;
            font-size: 13px;
            text-align: center;
          }
          
          .footer a {
            color: #e05d1f;
            text-decoration: none;
          }
          
          .footer a:hover {
            text-decoration: underline;
          }
          
          @media print {
            body {
              margin: 0.5in;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .card {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            
            .cause-item {
              break-inside: avoid;
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="logo-container">
              <img src="${baseUrl}/logo.webp" alt="Cognitive Bias Lab Logo" class="logo" onerror="this.src='data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22 viewBox=%220 0 100 100%22><text x=%2250%%22 y=%2250%%22 text-anchor=%22middle%22 dominant-baseline=%22middle%22 font-family=%22sans-serif%22 font-size=%2220%22 fill=%22%23e05d1f%22>CBL</text></svg>'; this.onerror=null;">
              <div class="title-container">
                <h2>Pre-Mortem Analysis Report</h2>
                <p class="subtitle">Anticipating obstacles to improve success rates</p>
              </div>
            </div>
            <div class="date">
              Generated on ${new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
          </div>
          
          <div class="card">
            <h2>Project Details</h2>
            <div class="detail-grid">
              <div class="detail-label">Project Name:</div>
              <div class="detail-value">${projectName}</div>
              
              <div class="detail-label">Project Goals:</div>
              <div class="detail-value">${projectGoals}</div>
              
              <div class="detail-label">Timeline:</div>
              <div class="detail-value">${projectTimeline}</div>
            </div>
          </div>
          
          <div class="card">
            <h2>Failure Scenario</h2>
            <p>${failureScenario}</p>
          </div>
          
          <div class="card">
            <h2>Potential Failure Causes & Mitigations</h2>
            <ul class="causes-list">
              ${causes.map((cause, index) => {
                const relatedBias = biases.find(bias => bias.id === cause.biasId);
                return `
                  <li class="cause-item">
                    <div class="cause-header">
                      <span class="cause-title">Potential Cause #${index + 1}</span>
                      ${relatedBias ? `
                        <a href="${baseUrl}/bias/${relatedBias.id}/" class="bias-tag" target="_blank">
                          ${relatedBias.name}
                        </a>
                      ` : ''}
                    </div>
                    <p class="cause-text">${cause.text}</p>
                    ${cause.mitigation ? `
                      <div class="mitigation">
                        <div class="mitigation-title">Mitigation Strategy:</div>
                        <p>${cause.mitigation}</p>
                      </div>
                    ` : ''}
                  </li>
                `;
              }).join('')}
            </ul>
          </div>
          
          <div class="footer">
            <p>This pre-mortem analysis was created using the 
              <a href="${baseUrl}/pre-mortem/" target="_blank">Pre-Mortem Analysis Tool</a> 
              by <a href="${baseUrl}" target="_blank">Cognitive Bias Lab</a>
            </p>
            <p>Learn more about cognitive biases and how they affect decision-making at 
              <a href="${baseUrl}" target="_blank">www.cognitivebiaslab.com</a>
            </p>
          </div>
        </div>

        <script>
          // Auto-print when loaded
          window.onload = function() {
            // Short delay to ensure styles are applied
            setTimeout(function() {
              window.print();
            }, 500);
          }
        </script>
      </body>
    </html>
  `;
  
  // Write the HTML to the new window and print it as PDF
  newWindow.document.open();
  newWindow.document.write(htmlContent);
  newWindow.document.close();
};

// Main PreMortem component
export const PreMortemPage: React.FC<PreMortemPageProps> = ({ biases }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [projectName, setProjectName] = useState("");
  const [projectGoals, setProjectGoals] = useState("");
  const [projectTimeline, setProjectTimeline] = useState("");
  const [failureScenario, setFailureScenario] = useState("");
  const [causes, setCauses] = useState<Cause[]>([{ text: "", biasId: "", mitigation: "" }]);
  const [isComplete, setIsComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // For selected scenario in Step 1
  const [selectedScenario, setSelectedScenario] = useState("");

  // Add useEffect to dispatch event for prerendering
  useEffect(() => {
    // Dispatch event to signal that the page is fully rendered for prerendering
    setTimeout(() => {
      // Create and dispatch the event for prerendering
      const event = new Event('app-fully-rendered');
      window.document.dispatchEvent(event);
      
      // Also dispatch a more specific event for this page
      const pageEvent = new Event('pre-mortem-page-loaded');
      window.document.dispatchEvent(pageEvent);
      
      // Add a ready indicator element for prerendering if not already present
      if (!document.getElementById('app-ready-indicator')) {
        const readyIndicator = document.createElement('div');
        readyIndicator.id = 'app-ready-indicator';
        readyIndicator.style.display = 'none';
        document.body.appendChild(readyIndicator);
      }
      
      console.log('[PreMortem] Page fully rendered, events dispatched');
    }, 2000); // Wait 2 seconds to ensure everything is loaded
  }, []);

  // Advanced step information
  const steps = useMemo(() => [
    { id: 0, title: 'Introduction', icon: Book },
    { id: 1, title: 'Project Details', icon: Brain },
    { id: 2, title: 'Imagine Failure', icon: History },
    { id: 3, title: 'Identify Causes', icon: Lightbulb },
    { id: 4, title: 'Summary', icon: Target }
  ], []);

  // Extract unique biases from causes - moved from renderCompletionScreen
  const identifiedBiases = useMemo(() => {
    const uniqueBiasIds = new Set(
      causes
        .filter(cause => cause.biasId)
        .map(cause => cause.biasId)
    );
    
    return Array.from(uniqueBiasIds)
      .map(biasId => biases.find(b => b.id === biasId))
      .filter((bias): bias is CognitiveBias => bias !== undefined);
  }, [causes, biases]);

  // Update the progress calculation to ensure it ends exactly at the end of each button
  useEffect(() => {
    // Calculate progress based on button positions rather than percentage
    // Each step represents 20% of the bar (5 steps total: 0,1,2,3,4)
    // For step 0: 0-20%, step 1: 20-40%, step 2: 40-60%, etc.
    const stepPercentage = 100 / steps.length;
    
    // Calculate end position of current button
    // For step 0, fill to 20%, for step 1, fill to 40%, etc.
    const progressValue = ((currentStep + 1) * stepPercentage);
    
    setProgress(progressValue);
  }, [currentStep, steps.length]);

  // Add loading state
  if (!biases || biases.length === 0) {
    return (
      <div className="min-h-screen bg-orange-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-center">
              <div className="text-gray-600">Loading pre-mortem tool...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Filter biases that are relevant for pre-mortem analysis
  const relevantBiases = biases.filter(bias => 
    ["Planning", "Decision-Making", "Memory", "Reasoning", "Social"].includes(bias.category)
  );

  // Sample scenarios
  const scenarios = [
    {
      title: "Mobile App Launch",
      description: "Launching a new mobile app in 3 months",
      detailedGoals: "Develop and launch a new mobile app with user account creation, core functionality, and basic analytics. The app should reach 10,000 downloads in the first month and maintain a 4+ star rating in app stores."
    },
    {
      title: "Website Redesign",
      description: "Complete website redesign to be finished in 6 months",
      detailedGoals: "Redesign the company website to improve user experience, increase conversion rates by 20%, and ensure mobile responsiveness. The new site should improve SEO rankings and reduce bounce rate from 65% to below 40%."
    },
    {
      title: "Marketing Campaign",
      description: "Holiday marketing campaign with a tight deadline",
      detailedGoals: "Create and execute a holiday marketing campaign across social media, email, and paid ads. Target a 30% increase in Q4 sales compared to last year with a budget of $50,000 and a positive ROI."
    }
  ];

  const addCause = () => {
    setCauses([...causes, { text: "", biasId: "", mitigation: "" }]);
  };

  const updateCause = (index: number, field: keyof Cause, value: string) => {
    const newCauses = [...causes];
    
    if (field === 'biasId' && value) {
      const selectedBias = biases.find(bias => bias.id === value);
      if (selectedBias && selectedBias.howToOvercome && selectedBias.howToOvercome.length > 0) {
        newCauses[index] = {
          ...newCauses[index],
          [field]: value,
          mitigation: selectedBias.howToOvercome[0].strategy
        };
      } else {
        newCauses[index] = {
          ...newCauses[index],
          [field]: value
        };
      }
    } else {
      newCauses[index] = {
        ...newCauses[index],
        [field]: value
      };
    }
    
    setCauses(newCauses);
  };
  
  const handleComplete = () => {
    setIsComplete(true);
  };

  const resetTool = () => {
    setCurrentStep(0);
    setProjectName("");
    setProjectGoals("");
    setProjectTimeline("");
    setFailureScenario("");
    setCauses([{ text: "", biasId: "", mitigation: "" }]);
    setSelectedScenario("");
    setIsComplete(false);
    setProgress(0);
  };

  // Render appropriate step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="text-base text-gray-600">
              <p>
                A pre-mortem is a strategic planning technique where you imagine that a project has already failed, 
                then work backward to determine what potentially could lead to the failure.
              </p>
              <p className="mt-2">
                This technique, developed by psychologist Gary Klein, helps identify risks and establish preventative measures
                at the beginning of a project rather than during a post-mortem after it's too late.
              </p>
            </div>
            
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-100 shadow-sm flex">
                <div className="bg-orange-100 rounded-xl p-2.5 w-10 h-10 flex items-center justify-center mr-3 shrink-0">
                  <Lightbulb className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-base font-medium text-gray-900">Overcome Cognitive Biases</h3>
                  <p className="text-base text-gray-600 mt-1">
                    Pre-mortems help combat planning fallacy, optimism bias, and overconfidence that can lead to project failures.
                  </p>
                </div>
              </div>
              
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-100 shadow-sm flex">
                <div className="bg-orange-100 rounded-xl p-2.5 w-10 h-10 flex items-center justify-center mr-3 shrink-0">
                  <Book className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-base font-medium text-gray-900">Research-Backed</h3>
                  <p className="text-base text-gray-600 mt-1">
                    Studies show that pre-mortems can increase the ability to identify potential problems by up to 30%.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 1:
        return (
          <div className="space-y-6">
            <p className="text-base text-gray-600">Start by describing the project you want to analyze.</p>
            
            <div className="mb-3">
              <p className="mb-1 text-base font-medium text-gray-500">Optional: select a sample scenario</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {scenarios.map((scenario, index) => (
                  <div 
                    key={index}
                    onClick={() => {
                      // If already selected, deselect it
                      if (selectedScenario === scenario.description) {
                        setSelectedScenario("");
                        setProjectName("");
                        setProjectGoals("");
                        setProjectTimeline("");
                      } else {
                        // Select and fill in all fields
                        setSelectedScenario(scenario.description);
                        setProjectName(scenario.title);
                        setProjectGoals(scenario.detailedGoals);
                        setProjectTimeline(scenario.title === "Mobile App Launch" ? "3 months" : 
                                          scenario.title === "Website Redesign" ? "6 months" : 
                                          "2 months");
                      }
                    }}
                    className={`relative rounded-lg border-2 p-3 cursor-pointer transition-all duration-300 ${
                      selectedScenario === scenario.description 
                        ? 'border-orange-400 bg-orange-50 shadow-sm' 
                        : 'border-gray-200 hover:border-orange-200 bg-white hover:bg-orange-50/30'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <p className="font-medium text-base text-gray-700">{scenario.title}</p>
                      {selectedScenario === scenario.description && (
                        <span className="text-base text-orange-500">✓</span>
                      )}
                    </div>
                    <p className="text-base text-gray-500 mt-0.5">{scenario.description}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="space-y-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div>
                <label className="block text-base font-medium text-gray-700">Project Name</label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  className="w-full pl-4 pr-3 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100/50 text-base transition-all duration-300 bg-white/80 backdrop-blur-sm hover:bg-white"
                  placeholder="e.g., Website Redesign Project"
                />
              </div>
              
              <div>
                <label className="block text-base font-medium text-gray-700">Project Goals</label>
                <textarea
                  value={projectGoals}
                  onChange={(e) => setProjectGoals(e.target.value)}
                  rows={3}
                  className="w-full pl-4 pr-3 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100/50 text-base transition-all duration-300 bg-white/80 backdrop-blur-sm hover:bg-white"
                  placeholder="What are you trying to achieve with this project?"
                />
              </div>
              
              <div>
                <label className="block text-base font-medium text-gray-700">Timeline</label>
                <input
                  type="text"
                  value={projectTimeline}
                  onChange={(e) => setProjectTimeline(e.target.value)}
                  className="w-full pl-4 pr-3 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100/50 text-base transition-all duration-300 bg-white/80 backdrop-blur-sm hover:bg-white"
                  placeholder="e.g., 3 months, Q4 2023, etc."
                />
              </div>
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-6">
            <div>
              <p className="text-base text-gray-700">
                <span className="font-medium">Instructions:</span> For this step, imagine that your project has completely failed. 
                It's 6 months in the future, and the project has been a disaster. Describe what happened.
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-base font-medium text-gray-700">Failure Scenario</label>
                <textarea
                  value={failureScenario}
                  onChange={(e) => setFailureScenario(e.target.value)}
                  rows={6}
                  className="w-full pl-4 pr-3 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100/50 text-base transition-all duration-300 bg-white/80 backdrop-blur-sm hover:bg-white"
                  placeholder="Describe in detail what went wrong with the project..."
                />
              </div>
              
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-100 shadow-sm">
                <h3 className="text-base font-medium text-gray-900">Research Insight</h3>
                <p className="text-base text-gray-700 mt-1">
                  This technique, known as "prospective hindsight," has been shown to increase the ability to identify 
                  reasons for future outcomes by 30% (Mitchell et al., 1989).
                </p>
              </div>
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="space-y-6">
            <p className="text-base text-gray-600">Based on your failure scenario, what could cause this outcome?</p>
            
            {causes.map((cause, index) => (
              <div key={index} className="p-6 border rounded-xl space-y-4 bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
                <div>
                  <div className="flex justify-between">
                    <label className="block text-base font-medium text-gray-700">Potential Cause #{index + 1}</label>
                    {index > 0 && (
                      <button
                        onClick={() => {
                          const newCauses = [...causes];
                          newCauses.splice(index, 1);
                          setCauses(newCauses);
                        }}
                        className="p-1.5 rounded-full text-gray-500 hover:text-red-500 hover:bg-red-50 transition-all"
                        aria-label="Remove cause"
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <textarea
                    value={cause.text}
                    onChange={(e) => updateCause(index, 'text', e.target.value)}
                    rows={2}
                    className="w-full pl-4 pr-3 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100/50 text-base transition-all duration-300 bg-white/80 backdrop-blur-sm hover:bg-white"
                    placeholder="What might lead to this failure?"
                  />
                </div>
                
                <div>
                  <label className="block text-base font-medium text-gray-700">Related Cognitive Bias</label>
                  <div className="relative">
                    <select
                      value={cause.biasId}
                      onChange={(e) => updateCause(index, 'biasId', e.target.value)}
                      className="w-full pl-4 pr-16 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100/50 text-base transition-all duration-300 bg-white/80 backdrop-blur-sm hover:bg-white appearance-none"
                      style={{ backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E")`, backgroundPosition: 'right 1rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em' }}
                    >
                      <option value="">Select a bias (optional)</option>
                      {relevantBiases.map(bias => (
                        <option key={bias.id} value={bias.id}>{bias.name}</option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                      {/* Chevron is already part of the background image */}
                    </div>
                  </div>
                </div>
                
                {cause.biasId && (
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-100 shadow-sm">
                    <p className="text-base text-gray-700">
                      <span className="font-medium text-gray-900">Mitigation Strategy:</span> {cause.mitigation || "No strategy available"}
                    </p>
                    <a
                      href={`/bias/${cause.biasId}/`}
                      className="mt-2 inline-flex items-center gap-1 text-orange-600 hover:text-orange-700 text-base font-medium"
                    >
                      Learn more about this bias <ArrowRight className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            ))}
            
            <button
              onClick={addCause}
              className="mt-4 flex items-center gap-1 font-medium text-orange-600 hover:text-orange-700 transition-colors"
            >
              <span className="text-lg">+</span> Add Another Cause
            </button>
          </div>
        );
      
      case 4:
        return (
          <div className="space-y-6">
            <div className="bg-white shadow-md rounded-xl overflow-hidden">
              <div className="bg-orange-50 px-6 py-5 border-b border-orange-100">
                <h3 className="text-lg font-medium text-gray-900">Pre-Mortem Results</h3>
                <p className="mt-1 max-w-2xl text-sm sm:text-base text-gray-600">
                  Review your pre-mortem analysis and develop an action plan.
                </p>
              </div>
              <div className="px-6 py-5">
                <dl className="divide-y divide-gray-200">
                  <div className="py-4 grid grid-cols-3 gap-4">
                    <dt className="text-sm sm:text-base font-medium text-gray-500">Project Name</dt>
                    <dd className="text-sm sm:text-base text-gray-900 col-span-2">{projectName}</dd>
                  </div>
                  <div className="py-4 grid grid-cols-3 gap-4">
                    <dt className="text-sm sm:text-base font-medium text-gray-500">Project Goals</dt>
                    <dd className="text-sm sm:text-base text-gray-900 col-span-2">{projectGoals}</dd>
                  </div>
                  <div className="py-4 grid grid-cols-3 gap-4">
                    <dt className="text-sm sm:text-base font-medium text-gray-500">Timeline</dt>
                    <dd className="text-sm sm:text-base text-gray-900 col-span-2">{projectTimeline}</dd>
                  </div>
                  <div className="py-4 grid grid-cols-3 gap-4">
                    <dt className="text-sm sm:text-base font-medium text-gray-500">Failure Scenario</dt>
                    <dd className="text-sm sm:text-base text-gray-900 col-span-2">{failureScenario}</dd>
                  </div>
                </dl>
              </div>
            </div>
            
            <div className="bg-white shadow-md rounded-xl overflow-hidden mt-6">
              <div className="bg-orange-50 px-6 py-5 border-b border-orange-100">
                <h3 className="text-lg font-medium text-gray-900">Potential Failure Causes & Mitigations</h3>
              </div>
              <div>
                <ul className="divide-y divide-gray-200">
                  {causes.map((cause, index) => {
                    const relatedBias = biases.find(bias => bias.id === cause.biasId);
                    return (
                      <li key={index} className="px-6 py-5 hover:bg-gray-50 transition-colors">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-sm sm:text-base text-gray-900">Potential Cause #{index + 1}</span>
                            {relatedBias && (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-orange-100 text-orange-800">
                                {relatedBias.name}
                              </span>
                            )}
                          </div>
                          <p className="text-sm sm:text-base text-gray-700">{cause.text}</p>
                          {cause.mitigation && (
                            <div className="mt-3 pl-4 border-l-2 border-orange-200">
                              <h4 className="text-sm sm:text-base font-medium text-gray-900">Mitigation Strategy:</h4>
                              <p className="text-sm sm:text-base text-gray-600 mt-1">{cause.mitigation}</p>
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
            
            <div className="flex justify-center mt-8">
              <AccentButton
                onClick={() => {
                  generatePreMortemPDF(
                    projectName,
                    projectGoals,
                    projectTimeline,
                    failureScenario,
                    causes,
                    biases
                  );
                  handleComplete();
                }}
                className="flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Export PDF
              </AccentButton>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  // Completion screen
  const renderCompletionScreen = () => {
    return (
      <div className="py-8">
        <div className="mx-auto bg-orange-100 rounded-xl p-3 w-16 h-16 flex items-center justify-center">
          <CheckCircle className="w-10 h-10 text-orange-500" />
        </div>
        <h3 className="mt-6 text-xl sm:text-2xl font-semibold text-gray-900 text-center">Pre-Mortem Completed!</h3>
        <p className="mt-3 text-sm sm:text-base text-gray-600 max-w-xl mx-auto text-center">
          You've successfully completed the pre-mortem analysis. By identifying potential causes of failure early, 
          you're better equipped to prevent them and improve your project's chances of success.
        </p>
        
        <div className="mt-8 flex justify-center gap-4">
          <AccentButton
            onClick={() => generatePreMortemPDF(
              projectName,
              projectGoals,
              projectTimeline,
              failureScenario,
              causes,
              biases
            )}
            className="flex items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Export PDF
          </AccentButton>
          
          <AccentButton
            onClick={resetTool}
            className="flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Start New Pre-Mortem
          </AccentButton>
        </div>
        
        {identifiedBiases.length > 0 && (
          <div className="mt-8 max-w-4xl mx-auto">
            <RelatedBiases biases={identifiedBiases} />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>Pre-Mortem Analysis Tool: Predict Project Failures | Cognitive Bias Lab</title>
        <meta name="description" content="Use our free pre-mortem analysis tool to identify potential project failures before they happen. Combat planning fallacy, optimism bias, and improve decision-making." />
        <meta name="keywords" content="pre-mortem, project planning, risk management, planning fallacy, cognitive bias, decision making, project failure prevention, Gary Klein" />
        <link rel="canonical" href="https://www.cognitivebiaslab.com/pre-mortem/" key="canonical" data-source="page-component" data-prerender-priority="high" />
        
        {/* OpenGraph tags for better social sharing */}
        <meta property="og:title" content="Pre-Mortem Analysis Tool: Predict Project Failures | Cognitive Bias Lab" />
        <meta property="og:description" content="Identify potential risks before your project starts. Our pre-mortem tool helps teams overcome cognitive biases that lead to planning failures." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cognitivebiaslab.com/pre-mortem/" />
        <meta property="og:site_name" content="Cognitive Bias Lab" />
        <meta property="og:image" content="https://www.cognitivebiaslab.com/pre-mortem-tool.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/webp" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pre-Mortem Analysis Tool: Predict Project Failures" />
        <meta name="twitter:description" content="Identify project risks early with our free pre-mortem tool. Learn how to overcome planning fallacy and other cognitive biases." />
        <meta name="twitter:image" content="https://www.cognitivebiaslab.com/pre-mortem-tool.webp" />
        <meta name="twitter:site" content="@CognitiveBiasLb" />
        
        {/* Structured data for rich search results */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Pre-Mortem Analysis Tool",
            "url": "https://www.cognitivebiaslab.com/pre-mortem/",
            "description": "A tool to help project teams identify potential failures before they happen by imagining the project has already failed.",
            "applicationCategory": "BusinessApplication",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "creator": {
              "@type": "Organization",
              "name": "Cognitive Bias Lab",
              "url": "https://www.cognitivebiaslab.com/"
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Project Managers, Team Leaders, Decision Makers"
            },
            "potentialAction": {
              "@type": "UseAction",
              "target": "https://www.cognitivebiaslab.com/pre-mortem/"
            }
          })}
        </script>
        
        {/* Additional SEO meta tags */}
        <meta name="author" content="Cognitive Bias Lab" />
        <meta name="robots" content="index, follow" />
        <meta name="theme-color" content="#f97316" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      </Helmet>

      <div className="min-h-screen bg-orange-50 py-12 text-base">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center space-y-6 mb-8">
              <GradientTitle className="text-3xl md:text-4xl mb-4" useH1={true}>
                Pre-Mortem Analysis Tool: Predict Project Failures
              </GradientTitle>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Identify potential problems before they happen by imagining your project has already failed. 
                Our research-backed tool helps you overcome planning fallacy and optimism bias.
              </p>
            </div>
            
            {!isComplete && (
              <div className="sticky top-4 z-30 mb-4">
                <div className="max-w-5xl mx-auto bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100 p-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-800">Pre-Mortem Process</h2>
                  </div>
                  
                  {/* Desktop step buttons - hidden on mobile */}
                  <div className="hidden md:flex justify-between items-center mt-4 gap-3">
                    {steps.map((step) => (
                      <button
                        key={step.id}
                        onClick={() => setCurrentStep(step.id)}
                        aria-current={currentStep === step.id ? 'step' : undefined}
                        className={`
                          px-4 py-2 
                          rounded-xl 
                          flex items-center 
                          justify-center 
                          gap-2 
                          min-w-0
                          flex-1
                          transition-all
                          duration-300
                          text-base
                          ${currentStep === step.id 
                            ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-md'
                            : 'bg-white/80 text-gray-600 hover:bg-orange-100/50'
                          }
                        `}
                      >
                        <step.icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                        <span className="truncate">{step.title}</span>
                      </button>
                    ))}
                  </div>
                  
                  {/* Progress bar */}
                  <div className="mt-4 relative">
                    <Progress 
                      value={progress} 
                      className="h-2 w-full rounded-full" 
                    />
                  </div>
                </div>
              </div>
            )}
            
            <div className="max-w-5xl mx-auto mb-8">
              {isComplete ? (
                <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-8">
                  {renderCompletionScreen()}
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-8">
                  <div className="bg-orange-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between flex-wrap">
                      <div className="flex items-center gap-3">
                        <div className="bg-orange-100 rounded-xl p-2.5 w-10 h-10 flex items-center justify-center">
                          {React.createElement(steps[currentStep].icon, { 
                            className: "w-5 h-5 text-orange-500" 
                          })}
                        </div>
                        <h2 className="text-xl font-semibold text-gray-900">
                          Step {currentStep}: {steps[currentStep].title}
                        </h2>
                      </div>
                    </div>
                  </div>
                  
                  <div className="px-6 py-5">
                    {renderStepContent()}
                  </div>
                  
                  <div className="bg-gray-50 px-6 py-4 flex justify-between">
                    <OutlineButton
                      onClick={() => setCurrentStep(prev => Math.max(prev - 1, 0))}
                      disabled={currentStep === 0}
                      className={currentStep === 0 ? "opacity-50 cursor-not-allowed" : ""}
                      active={false}
                    >
                      Previous
                    </OutlineButton>
                    
                    <AccentButton
                      onClick={() => {
                        if (currentStep === 4) {
                          handleComplete();
                        } else {
                          setCurrentStep(prev => Math.min(prev + 1, 4));
                        }
                      }}
                    >
                      {currentStep === 4 ? 'Complete' : 'Next'}
                    </AccentButton>
                  </div>
                </div>
              )}
              
              <div className="space-y-4">
                <div className="flex items-center justify-center text-center gap-3">
                  <div className="bg-orange-100 p-2 rounded-lg">
                    <BookOpen className="w-5 h-5 text-orange-600" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 text-center leading-tight hyphens-auto">Academic References</h2>
                </div>
                
                <div className="bg-white rounded-2xl p-5 border border-orange-100">
                  <ul className="space-y-4">
                    <li className="flex gap-3">
                      <span className="text-orange-500 mt-1.5 h-2 w-2 rounded-full bg-orange-500 shrink-0" />
                      <span className="text-sm sm:text-base text-gray-600 break-words">Klein, G. (2007). Performing a project premortem. Harvard Business Review, 85(9), 18-19.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="text-orange-500 mt-1.5 h-2 w-2 rounded-full bg-orange-500 shrink-0" />
                      <span className="text-sm sm:text-base text-gray-600 break-words">Mitchell, D. J., Russo, J. E., & Pennington, N. (1989). Back to the future: Temporal perspective in the explanation of events. Journal of Behavioral Decision Making, 2(1), 25-38.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="text-orange-500 mt-1.5 h-2 w-2 rounded-full bg-orange-500 shrink-0" />
                      <span className="text-sm sm:text-base text-gray-600 break-words">Veinott, B., Klein, G. A., & Wiggins, S. (2010). Evaluating the effectiveness of the PreMortem technique on plan confidence. Proceedings of the 7th International ISCRAM Conference.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}; 